// Canonical content for the Programs page programs_explorer widget: the 24
// B2C programs from the Notion "Programs & Playbooks" page, tier for tier and
// in the same order as its diagram (2026-09-05, Bill; Backorders and the four
// brand authorizations added 2026-10-25). Source of truth for
// /create-deal-room and the /api/internal/migrate-programs endpoint. Updates
// here roll out to every room on the next page load (rooms that still store
// their own programs[] need a forced migrate-programs run first).
//
// Each program body is the Notion page rewritten for the supplier reading
// it: the intro, purpose, how it works, standard terms, who does what, and
// what good looks like. Internal sections (when not to sell it, pitfalls,
// skills, other suppliers' names, internal margin targets) stay in Notion.
// Tables are lists because the room's markdown renderer has no table support.
//
// Per-room overrides (hides, Recommended flags) live in the stored room's
// programOverrides, NOT here. Which programs a supplier has enabled lives in
// the room state sidecar (programStatus), not in the room JSON.

import type { WidgetOfType } from "../schema/widgets";

// The pre-selected sentence assumes /create-deal-room pre-selected a starting set
// (room state programStatus) when it built the room (Bill, 2026-09-06). The
// "tick or untick, saved for everyone" line that followed it was removed on
// 2026-09-07 (Bill). The closing two sentences were added on 2026-09-08 after a
// supplier-persona review read a pre-ticked tile as consent (Bill).
export const CANONICAL_PROGRAMS_PAGE_INTRO =
    "These programs show how we grow with our suppliers step by step. We start by building a solid foundation with the basics like damage and defects, returns and advertising, then move into growth and scale tiers that boost sales and visibility. Each level strengthens the partnership and increases the profit we share. We have pre-selected the programs we think fit you best, based on partners like you in your category. A tick is a shortlist. Nothing is agreed until it is written into the Program Agreement.";

type Program = WidgetOfType<"programs_explorer">["props"]["programs"][number];

const FOUNDATION: Program[] = [
    {
        id: "damage-defects",
        title: "Damage & Defects Program",
        tier: "foundation",
        icon: "heart-broken.svg",
        summary:
            "A supplier-funded allowance that covers the cost Bison absorbs when a customer receives a unit that is broken, defective, or otherwise not sellable. One of the three components of every supplier's base allowance, alongside Returns and Marketing.",
        body: `## Purpose

Damage is a structural cost of selling big-and-bulky furniture online, not a sign of a weak partnership. The allowance shares that cost fairly so a handful of broken units never put a SKU underwater, which keeps pricing competitive and protects the Buy Box on your behalf.

## How it works

1. The allowance is set in your agreement as a percentage of shipped supplier cost (or as per-event credits).
2. A daily job accrues the earned allowance against actual orders.
3. At period end (monthly, quarterly, or yearly), Bison generates a credit request for the amount earned.
4. The credit is collected once the agreed window elapses (typically 30 days).
5. Real damage and defect events are tracked case by case, so earned allowance can always be measured against actual cost.

## What the allowance covers

- Claim processing when a customer reports a damaged or defective unit
- Replacement coordination (shipping a new unit, partial refunds, concessions)
- Disposal or write-off of units that cannot be resold

## Standard rates

- **Ground-shipped catalog:** 3%, the base program target.
- **LTL / freight-heavy catalog:** 4%, a 1% LTL surcharge, because freight handling drives higher damage incidence.

## Who does what

- **Setup.** Bison enters and maintains the allowance agreement. You agree the rate and payment method.
- **Tracking.** Bison logs every damage and defect event and accrues earned allowance daily. You review periodic reporting.
- **Settlement.** Bison generates the period-end credit request. You issue the credit memo or allow deduction from payment.
- **Root cause.** Bison flags the SKUs or warehouses behind the largest damage cost. You improve packaging or product where a SKU is a repeat offender.`,
    },
    {
        id: "returns",
        title: "Returns Program",
        tier: "foundation",
        icon: "undo.svg",
        summary:
            "A supplier-funded allowance that covers what Bison spends handling customer returns: reverse logistics, inspection, restocking, and reselling or disposing of returned units. It sits alongside Damage & Defects and Marketing as one of the three components of your base allowance.",
        body: `## Purpose

Returns are a normal cost of marketplace selling, and on furniture they are unavoidable. The allowance shares the operational cost so Bison can keep prices competitive and absorb returns without a SKU tipping into the red, while return-reason data points both partners at the real fixes.

## How it works

1. A returns allowance is set in your agreement as a percentage of shipped supplier cost.
2. Every return is logged with its reason and disposition (restocked, liquidated, or written off).
3. A daily job accrues earned allowance, and Bison generates a period-end credit request the same way the Damage & Defects program does.
4. Suppliers who cannot logistically accept returns can opt into a No-Returns (DFI) agreement, where Bison takes on 100% of return risk in exchange for a higher rate.

## Standard rates

- **Standard returns allowance:** 2%. The base program target; you still accept returns.
- **No-Returns (DFI), ground:** 2-3%. Replaces the 2% base; Bison handles all returns.
- **No-Returns (DFI), LTL:** 3-4%. Higher freight handling on returned freight.

The allowance offsets Bison's operational cost of a return. The customer refund itself is a separate concession, not something the allowance reimburses.

## Who does what

- **Handling.** Bison manages reverse logistics, inspection, restocking, and resale or disposal. You accept returns (standard model) or cede full control (DFI).
- **Data.** Bison captures return reason and disposition per unit. You act on root-cause signals in product or content.
- **Settlement.** Bison generates the period-end credit request. You issue the credit memo or allow deduction.`,
    },
    {
        id: "promotions",
        title: "Promotions Program",
        tier: "foundation",
        icon: "megaphone.svg",
        summary:
            "Covers the discounts, coupons, and deal events Bison runs on your products to drive sales velocity, win the Buy Box back on contested listings, and move inventory. Promotions can be Bison-led (an account manager builds a campaign and invites suppliers in) or supplier-initiated.",
        body: `## Purpose

A well-timed promotion is one of the fastest ways to build sales velocity, climb Best Seller Rank, and signal demand to the marketplace algorithm. Run with discipline and paired with advertising, a promotion buys lasting rank, not just a one-day spike.

## How it works

1. A promotion is defined by its date range, discount type and depth, and the SKUs it covers.
2. Bison-led campaigns are published to a calendar and offered to relevant suppliers, who opt in with their product selection and discount.
3. A SKU is on promo when its current cost sits below its standard cost; discount depth is the gap between the two.
4. Performance is measured against a non-promo baseline: revenue per day, units, sell-through, and margin after advertising.

## What good looks like

- **Sell-through:** 40%+ of promoted SKUs sell, which shows the SKU selection and discount were well chosen.
- **ROAS on ad-backed promos:** 8x or higher, strong enough that Bison may top up spend to capture more volume.
- **Revenue per day vs baseline:** a measurable lift, so the promo drives incremental volume rather than discounting existing demand.

## Who does what

- **Design.** Bison sets discount depth, dates, and eligible SKUs, and publishes the calendar. You opt in and select which products to promote.
- **Funding.** Bison pairs promos with advertising and tops up spend when ROAS is strong. You approve promotional pricing and any MAP breaks.
- **Measurement.** Bison tracks lift vs baseline and iterates the next campaign. You review performance and align on cadence.`,
    },
    {
        id: "advertising",
        title: "Advertising Program",
        tier: "foundation",
        icon: "tv-3.svg",
        summary:
            "Bison's managed Amazon Ads service, co-funded by a supplier marketing allowance. Bison builds and optimizes Sponsored Products, Sponsored Brands, and display campaigns on your behalf, turning ad spend into product visibility, sales velocity, and rank.",
        body: `## Purpose

Advertising is how a good product gets found. You fund a marketing allowance, Bison runs the campaigns with daily optimization, and when a campaign performs, Bison invests beyond the allowance from its own margin to capture every profitable sale available.

## How it works

1. You commit a marketing allowance, typically 3% of shipped cost, in the agreement.
2. Bison builds and manages the campaigns through its internal AdOps console, with daily bid, placement, and search-term optimization and an audit log on every change.
3. Ad spend and performance are tracked per supplier: spend, ACOS, ROAS, TACOS, CTR, and CPC.
4. When a campaign clears a strong ROAS bar, Bison tops up the daily budget beyond your allowance and funds the difference itself, because the incremental sales are profitable.

## Standard terms

- **Marketing allowance:** 3% of shipped cost, a standard component of the base allowance.
- **Bison top-up threshold:** ROAS of 8x or higher. Above this, Bison spends beyond the allowance and funds the difference.

## What good looks like

- **ROAS** at or above 8x is the bar where Bison leans in with more budget.
- **ACOS** at or below 5% is very healthy; up to 10% is acceptable; above 10% needs attention.
- **TACOS** trending down over time means advertising is becoming a smaller share of total sales: the listing is earning more organic demand.

## Who does what

- **Campaigns.** Bison builds, optimizes, and prunes all campaigns across marketplaces. You commit the marketing allowance rate.
- **Content.** Bison selects keywords and placements. You provide accurate titles, images, and bullets that ads point to.
- **Reporting.** Bison delivers a monthly performance report (spend, ACOS, ROAS, TACOS). You review and align on strategy.`,
    },
    {
        id: "clearance",
        title: "Clearance Program",
        tier: "foundation",
        icon: "discount-2.svg",
        summary:
            "The structured way Bison moves slow-moving, aged, or overstock inventory off the shelf: targeted markdowns, removal orders back to you, or marketplace liquidation. The aim is to free up capital and warehouse space while recovering as much value as possible.",
        body: `## Purpose

Aged inventory costs money every month it sits, in storage fees and tied-up capital. A disciplined clearance motion turns dead stock back into cash and warehouse space, and the pattern of what needs clearing points straight at the forecasting and listing fixes that prevent it next time.

## How it works

1. **Detection.** SKUs are flagged for clearance review once they cross an age threshold or fall below a turnover floor.
2. **Disposition.** Bison chooses the lowest-loss path for each SKU: mark the price down to move units through the existing listing, place a removal order to pull units out of FBA and return them to you, or liquidate through the marketplace at a steep discount when markdown and removal do not pencil.
3. **Cost recovery.** Bison nets removal and liquidation fees against recovered value and reports the outcome.

## Who does what

- **Identification.** Bison flags clearance candidates and picks the disposition. You review candidates and agree the approach.
- **Execution.** Bison manages markdown, removal, or liquidation logistics. You accept removal returns or fund a clearance allowance where agreed.
- **Prevention.** Bison surfaces the upstream cause (forecast, price, listing). You adjust production or assortment to reduce future overstock.`,
    },
    {
        id: "b2b",
        title: "B2B Program",
        tier: "foundation",
        icon: "briefcase-24.svg",
        summary:
            "Opens the Amazon Business channel for your products: business-customer pricing, quantity breaks, and B2B-specific listing treatment that reach procurement buyers alongside regular consumers. At the Foundation tier this is about switching the channel on; the deeper B2B suite builds on top of it.",
        body: `## Purpose

Business buyers shop the same marketplace but buy differently: larger baskets, repeat orders, and a procurement mindset. Turning on B2B opens that demand at materially higher conversion, with no new listings to build and no new channel to learn.

## How it works

1. Eligible products are flagged as B2B-eligible and surfaced to Amazon Business buyers.
2. Bison configures quantity-tiered pricing so larger orders earn a deeper unit price (for example, breaks at 11+ and 51+ units).
3. B2B activity is tracked alongside, but distinct from, consumer sales so the margin effect of tiered pricing stays visible.
4. As you grow into the channel, the higher B2B tiers layer on managed quotes, vertical pricing, and a curated business assortment.

## The headline benefit

Amazon Business converts at roughly 3x the rate of the consumer channel. Procurement workflows, bulk-order economics, and business-only pricing mean a B2B-eligible listing turns far more of its traffic into orders.

## Who does what

- **Setup.** Bison enables the channel and configures quantity-tier pricing. You approve B2B pricing tiers and any volume commitments.
- **Listings.** Bison optimizes content for procurement buyers where it helps. You provide accurate product and packaging data.
- **Tracking.** Bison reports B2B sales and margin separately from consumer. You review channel performance.`,
    },
];

const GROWTH: Program[] = [
    {
        id: "rebate",
        title: "Rebate Program",
        tier: "growth",
        icon: "trophy.svg",
        summary:
            "Rewards you for growth. As your annual purchase volume with Bison climbs through defined tiers, you earn back a rising percentage of qualified revenue. It is the Growth-tier program that turns a bigger partnership into a better unit economic for you.",
        body: `## Purpose

A rebate aligns both sides around the same goal: sell more together. The more volume the partnership drives, the more you earn back, which funds the next round of investment and makes growth self-reinforcing.

## How it works

1. Tiers and rates are written into your allowance agreement, keyed to annual purchase volume.
2. The agreement specifies the rebate type, which changes the payout materially. **Paid to the First:** the higher tier rate applies to all revenue from the first dollar once a threshold is crossed (retroactive). **Growth:** the rate applies only to revenue inside that tier's band.
3. Qualified revenue is calculated by an agreed method (item cost, item cost with credits, or item cost with credits and allowances).
4. Credits are generated when cumulative purchases cross a tier (if enabled) or reconciled at agreement end. Revenue accumulation resets each period.

## Standard tiers

- **$100K to $250K** annual purchase volume: 0.5%
- **$250K to $500K:** 1.0%
- **$500K to $1M:** 1.5%
- **$1M+:** 2.0%

## Who does what

- **Tracking.** Bison accumulates qualified revenue, applies tier thresholds, and generates credits. You drive the volume that advances the tier.
- **Reconciliation.** Bison settles earned vs already-received at period end to avoid double-counting. You review the rebate statement.

## What good looks like

- You advance tiers period over period, the clearest sign of a growing, healthy partnership.
- Year-to-date revenue tracks ahead of the threshold needed to reach the target tier.`,
    },
    {
        id: "white-label",
        title: "White Label Program",
        tier: "growth",
        icon: "tag.svg",
        documents: [{ title: "White Label Program", url: "/program-docs/white-label-program.pdf" }],
        summary:
            "Sells your manufactured products under a Bison-owned Amazon brand. You still make the goods; Bison creates and owns the listing, the brand, the content, and the advertising. White-label SKUs live on their own ASINs, separate from any supplier-branded listing.",
        body: `## Purpose

White label lets Bison build durable, owned brand equity on top of great products, capturing demand your own brand may not reach and earning a higher margin that funds aggressive growth.

## How it works

Every white-label product moves through a six-stage advertising lifecycle:

1. **Build.** A new SKU enters a monthly auto-discovery campaign to find what converts.
2. **Validate.** Early demand confirmed; winners separated from non-starters.
3. **Scale.** Spend concentrated on proven winners to build velocity and rank.
4. **Defend.** Rank and Buy Box protected on established sellers.
5. **Zombie / Dead.** Non-performers wound down so spend stays on what works.

Listings live under Bison brands such as Starlight Canyon Home, Outdoor Escape, FurniFact, and Bison Office. Reviews, Q&A, and Buy Box are tracked per ASIN, separate from any supplier-branded variant. White-label ad spend and revenue are reported separately every week and month.

## Standard terms

- **White-label allowance premium:** 1-2% above base. Covers brand development, packaging, and enhanced content.

## Who does what

- **Brand and listings.** Bison owns the brand, creates and optimizes listings, and sets the category. You provide product, images, and specs.
- **Advertising.** Bison runs the full six-stage ad lifecycle and manages spend. You meet the demand the winners generate.
- **Reporting.** Bison tracks revenue, margin after ads, and the white-label pipeline. You align on assortment and supply.`,
    },
    {
        id: "bundles-and-sets",
        title: "Bundles and Sets Program",
        tier: "growth",
        icon: "box-2.svg",
        summary:
            "Groups related SKUs into a single new product: a dining set, a coordinated bedroom group, a multi-piece accessory pack. Each bundle becomes its own ASIN with combined content and a composite SKU. It is a low-cost way to create fresh, higher-value listings out of inventory you already have.",
        body: `## Purpose

A well-built set sells the room, not the piece. Bundles lift average order value, give customers an easy "buy it all" option, and create brand-new listings (with their own keywords and rank) without any new product being manufactured.

## When it fits

- You have complementary SKUs that customers naturally buy together (table and chairs, dresser and nightstands, a set of coordinated pieces).
- Individual pieces are healthy but average order value is low and there is room to sell up.
- You want incremental listings and keyword coverage without new product development.

## How it works

1. Related SKUs are identified, often by product family, color, or pattern, using Bison's grouping tool.
2. Pieces are grouped into a set, with the group reviewed and adjusted by hand where the automatic match is imperfect.
3. Each group is published as a new bundled ASIN with combined product data, imagery, and a composite name and SKU.
4. The bundle is then run like any other listing: optimized, advertised, and measured on its own performance.

## Who does what

- **Curation.** Bison identifies which SKUs bundle well and builds the sets. You confirm which combinations make sense and are buildable.
- **Listings.** Bison creates the bundled ASIN, content, and imagery. You supply component data and any set-specific assets.
- **Fulfillment.** Bison configures how the set ships and is tracked. You ensure component availability so the bundle stays in stock.

## What good looks like

- Bundled listings carry a higher average order value than the component pieces sold alone.
- The set adds incremental sales rather than simply shifting demand off the single-piece listings.
- Components stay co-available, so the bundle rarely goes out of stock on a single missing piece.`,
    },
    {
        id: "listing-optimization",
        title: "Listing Optimization Program",
        tier: "growth",
        icon: "gauge-2.svg",
        documents: [{ title: "AI Image Generation", url: "/program-docs/ai-image-generation.pdf" }],
        summary:
            "Improves your Amazon listings so they get found and convert: better titles, bullets, descriptions, backend keywords, images, and A+ content. It runs as a two-stage funnel, a catalogue-wide audit that finds the biggest opportunities, then a deep rewrite on the listings worth the investment.",
        body: `## Purpose

A great product loses sales every day to a weak listing. Optimization turns existing demand into orders without spending a dollar more on advertising, and it compounds: a stronger listing converts the ad traffic the Advertising Program already pays for.

## When it fits

- Your listings have weak content, thin images, or poor keyword coverage relative to category competitors.
- Traffic is arriving but converting poorly, or the listing is not being found for the searches that matter.
- You are about to invest in advertising and want the destination listing ready to convert before spend ramps.

## How it works

1. **Audit.** A wide scoring pass across the whole catalogue ranks the biggest opportunities, every ASIN included.
2. **Full rewrite.** A full rewrite on the top picks, typically 10-30 ASINs per batch, with image review, competitor keyword analysis, review-theme closure, and a projected score uplift.

Every listing is scored on a shared two-part model: **Hygiene** (is the listing complete and correct) and **Performance** (will it be found and will it convert). The full rewrite produces a fully rewritten listing with a projected before-and-after score.

## Content production

Strong listings need strong assets. The program also covers the imagery and video that feed an optimized listing: AI image generation, lifestyle photography, and videography.

## Who does what

- **Analysis.** Bison audits the catalogue, picks the high-opportunity ASINs, and runs the full rewrite. You review the prioritized list.
- **Rewrite.** Bison composes the new title, bullets, keywords, and image guidance in your brand voice. You approve the rewrite and supply new assets where needed.
- **Data.** Bison fixes backend attributes, filter terms, and dimensions. You resolve errors in your own content submissions.

## What good looks like

- A measurable Hygiene uplift (often 10 to 20 points) and a Performance lift on the rewritten ASINs.
- Listings found for the keywords that carry search demand beyond the brand name.`,
    },
    {
        id: "exclusivity",
        title: "Exclusivity Program",
        tier: "growth",
        icon: "crown.svg",
        documents: [{ title: "Exclusivity Program", url: "/program-docs/exclusivity-program.pdf" }],
        summary:
            "Makes Bison Commerce the sole marketplace seller for selected SKUs. One partner, full control, zero hassle: we regain price control for you and reinvest the additional margin into advertising your products to increase sales.",
        body: `## How it works

A five-stage process from deal to profit:

1. **Exclusivity deal** (2-4 weeks). Align on goals, select products (Head / Core / Tail), sign the exclusivity agreement.
2. **Content update** (2-4 weeks). Product images, descriptions, bullet points, and A+ content created or optimized.
3. **Pricing and ad strategy** (1-2 weeks). Pricing and ad strategies developed for exclusive SKUs to drive visibility.
4. **Growth** (3-9 months). Velocity built by focusing investment on the right SKUs with competitive pricing.
5. **Profit** (ongoing). Once desired velocity is hit, focus shifts to long-term profitability.

## Why partners prefer exclusivity

- **Regain price control.** Set and maintain consistent pricing across sales channels, prevent race-to-the-bottom discounts, protect brand value.
- **Increase sales.** Raise sales by consolidating demand, increasing visibility and removing disruptive competitors.
- **Improve ad spend efficiency.** Targeted campaigns and a unified strategy lead to better returns and less waste.
- **Simplify partnership management.** A single focused partner handles all aspects of pricing, marketplace growth, and operations.
- **Consistent brand experience.** Same quality, support, and messaging across every channel.
- **Faster testing and iteration.** Try new ideas, promotions, and products with quick feedback.

## SKU segmentation (Head / Core / Tail)

- **Head: defend and monetize.** Protect price and maximize margin on top sellers. 50% of budget; 15-35% sales increase expected; 3-4 months to results.
- **Core: scale efficiently.** Scale volume while keeping profitability. 30% of budget; 10-30% sales increase; 6-9 months.
- **Tail: develop new winners.** Prove demand and build future winners. 20% of budget; 5-15% sales increase; 12-18 months.

Start with Head and Core SKUs for immediate lift, then focus on Tail to build the next wave.

## Standard financial terms

- **Marketing allowance:** 2-3% (minimum $1,500 per month). Co-op advertising, listing optimization, promotions. May be used as direct ad spend or indirect investment.
- **Damage and defects:** 3-5%. Covers damage cases and returns. 3% ground, 4-5% LTL-heavy catalogs.
- **Exclusivity premium:** 1-2%. Additional allowance for exclusive 3P distribution rights.
- **Volume rebate:** 0.5-2%. Tiered by quarterly or annual purchase volume.

## Who does what

- **Listings.** Bison creates, optimizes, and manages listings and storefront. You provide full and accurate product data.
- **Competition.** Bison monitors listings for compliance and suppressions. You remove any reseller competitors.
- **Pricing.** Bison manages all pricing across channels. You resolve third-party retailer pricing that suppresses the Buy Box.
- **Marketing.** Bison manages all advertising and promotions. You contribute to product content improvements.
- **Operations.** Bison supports new product launches and provides regular reporting. You notify us of inventory, pricing, or assortment changes.
- **Communication.** Bison maintains ongoing communication and listing recommendations. You cooperate on updates and strategies.

## Performance KPIs

Available in the Bison Commerce Supplier Portal: sales volume, average selling price, advertising spend, TACOS, ROAS, Best Seller Rank, stock-out rate, and suppressed revenue.

## Case study

Secured exclusivity on selected products, becoming the only seller on their listings. After launching ads and upgrading content, sales grew 2.4x from Q1 2024 to Q3 2025, supported by $192K of ad spend.`,
    },
    {
        id: "1p-to-3p-conversion",
        title: "1P to 3P Conversion Program",
        // Scale since 2026-10-25 (Bill): last in the tier, so it reads as a later step.
        tier: "scale",
        icon: "arrows-opposite-direction-x.svg",
        summary:
            "Moves you from selling first-party (1P, wholesaling to Amazon through Vendor Central) to selling third-party (3P, through Bison as the marketplace seller). You trade Amazon's wholesale squeeze for pricing control, brand ownership, and higher margin, and Bison handles the hard parts of the transition.",
        body: `## Purpose

Under 1P, Amazon owns the price, the buying decisions, and the restock cadence, and squeezes the wholesale rate over time. Converting to 3P through Bison hands pricing and brand control back to you, lifts margin, and ends the cycle of listings going dark whenever Amazon decides not to reorder.

## When it fits

- Amazon is the dominant or sole seller and the brand depends on Amazon's purchase orders.
- Availability crashes whenever Amazon stops restocking, taking rank and reviews down with it.
- You lack internal 3P expertise and want a partner to run the channel.
- There is meaningful review and ASIN equity at risk that a botched migration would destroy.

## How it works

1. **Assess the fit** using the marketplace signals above.
2. **Bison takes the hard parts:** trademark and brand registry, compliance, and listing creation. You provide the product; Bison does the rest.
3. **Preserve equity.** The migration reuses existing ASINs so review history, rank, and ad-campaign learning carry over. A fresh 3P listing would start at zero, which is exactly what conversion avoids.
4. **Launch and ramp** with a dedicated new-supplier launch fund covering the higher costs of the first 90 days (listing creation, content, compliance, initial advertising).

## Standard terms

- **Base allowance:** 8% (3% damage and defects + 2% returns + 3% marketing). The standard 3P cost structure applies post-conversion.
- **New supplier launch fund:** 2-3% for the first 90 days. Covers ramp costs; expires after the launch window.

## Who does what

- **Migration.** Bison handles brand registry, compliance, and listing creation, and preserves ASINs. You transition supply from wholesaling to Amazon to fulfilling through Bison.
- **Launch.** Bison invests in content and advertising through the ramp. You ensure availability and align on the new pricing strategy.

## What good looks like

- Post-conversion revenue exceeds the prior 1P revenue, on both margin and volume.
- Consistent availability replaces the old restock-dependent stockout pattern.
- ASIN review count is preserved, with new reviews accumulating on the same listings.
- Bison holds the Buy Box, rather than competing against your own product.`,
    },
    // The four brand authorizations below (Bill, 2026-07-30 in the supplier
    // analysis ladder; on the deal room 2026-10-25) are standing permissions,
    // not programs with terms, so their titles drop the "Program" suffix.
    {
        id: "trademark",
        title: "Trademark",
        tier: "growth",
        icon: "copyright.svg",
        summary:
            "You share the trademark registration for each brand Bison sells, and Bison keeps it on file. It is the proof a marketplace asks for when a listing is challenged or a brand enrolls in Brand Registry.",
        body: `## Purpose

Marketplaces ask for the trademark when a listing is challenged, when another seller copies it, and when a brand enrolls in Brand Registry. With your registration on file, Bison answers from the record instead of asking you for paperwork while a listing is down.

## How it works

1. **Share the registration.** You send the registration or serial number and the certificate for each brand (USPTO, or the trademark office where it is filed).
2. **Bison records it** in its trademark register: the mark, the owner, the goods it covers and the number.
3. **Bison uses it** to answer IP complaints, brand-authorization requests and copied listings on your products.
4. **It unlocks the next steps:** Brand Registry, then the Reseller and Brand Representative roles.

## If the brand is not registered yet

Filing comes first. An application needs the mark (the word, or the logo as an image), its class (furniture is usually class 20), a specific description of the goods, the date of first use and a photo of the mark on the product or packaging. A pending application is enough to start Brand Registry.

## Who does what

- **Registration.** You own and maintain the trademark and send the registration. Bison keeps the record current.
- **Enforcement.** Bison cites the registration in complaints and authorization requests. You confirm ownership when a marketplace contacts the owner directly.

The trademark stays yours. Bison holds a copy for authorization, never ownership.`,
    },
    {
        id: "brand-registry",
        title: "Brand Registry",
        tier: "growth",
        icon: "certificate.svg",
        summary:
            "You enroll your brand in Amazon Brand Registry and connect Bison's selling account. That lets Bison build A+ Content and a brand Store, run Sponsored Brands ads and protect your listings on your behalf.",
        body: `## Purpose

Without Brand Registry, your listings run on the basic product page and the basic ad types. Enrollment opens richer product pages, brand ads and control over who edits the listing.

## What you need first

- A pending or registered trademark for the brand.
- A Seller Central or Vendor Central account to enroll from.

## How it works

1. **Enroll the brand.** Log into Brand Registry with your Seller or Vendor Central credentials and choose "Enroll a brand".
2. **Brand and trademark details.** The brand name exactly as it reads on the certificate, the trademark office, the number, the status (registered or pending) and the type (word or design mark).
3. **Brand attributes.** At least one product category, and at least one unaltered photo of the product or packaging with the brand name permanently on it.
4. **Manufacturing and distribution.** How and where the product is made and how it is distributed.
5. **Verification.** Amazon may send a code to your trademark correspondent, often your attorney. It has to be entered in the case within 10 days, so let them know before you submit.
6. **Connect Bison.** Once approved, assign Bison's selling account a role: the Reseller Role at minimum, the Brand Representative Role for the full toolset.

Your supplier manager sends the Bison Commerce Brand Registration Guide, which walks through each screen.

## Who does what

- **Enrollment.** You enroll the brand as its owner and answer Amazon's verification. Bison sends the guide and works through the steps with you.
- **Brand tools.** Bison builds A+ Content and the brand Store and runs Sponsored Brands campaigns. You assign Bison's account its role.

## What good looks like

- The brand enrolled, with Bison connected through a selling role.
- A+ Content and a brand Store live on your best sellers.
- Sponsored Brands campaigns running alongside Sponsored Products.`,
    },
    {
        id: "reseller-role",
        title: "Reseller Role",
        tier: "growth",
        icon: "user-check.svg",
        summary:
            "The Brand Registry role for an authorized seller. Your Registry Administrator assigns it to Bison's selling account in a few minutes, at no cost, and it lets Bison run brand ads for you.",
        body: `## Purpose

The role tells Amazon that Bison sells your brand with your consent. It unlocks the brand ad types for Bison's campaigns without handing over control of the brand. It is the minimum Bison needs before advertising starts, and the Checklist asks for it during onboarding.

## What it unlocks

- **Yes:** Sponsored Brands and Sponsored Display ads for your brand.
- **No:** the full Brands menu, Brand Analytics, authority over listing content and the brand protection tools. Those come with the Brand Representative Role.

The details vary a little by brand and category.

## How to grant it

1. Your Brand Registry Administrator opens **Manage, then Manage Selling Accounts, then Connect a selling account**.
2. They enter Bison's merchant token (your supplier manager sends it) and assign the **Reseller** role.

No cost and no change to the agreement.

## Who does what

- **Access.** Bison sends the merchant token and the steps. Your Registry Administrator assigns the role.
- **Use.** Bison runs Sponsored Brands and Sponsored Display for the brand. You stay the Administrator and keep control of the brand.`,
    },
    {
        id: "brand-representative-role",
        title: "Brand Representative Role",
        tier: "growth",
        icon: "user-star.svg",
        summary:
            "The higher Brand Registry role, for a partner acting on behalf of the brand. It gives Bison your brand's search and repeat-purchase data and authority over the listing content it already maintains for you.",
        body: `## Purpose

With this role, Bison's keyword and listing work runs on your brand's own Amazon data, and the content Bison maintains holds against edits from other sellers. Bison can also report copied listings and unauthorized sellers without waiting on you.

## What it unlocks, beyond the Reseller Role

- **Brand Analytics:** the search terms shoppers use to find your products, repeat purchase and what else they buy.
- **Authoritative listing content:** Bison's edits to your listings outrank other sellers'.
- **Brand protection tools,** including Report a Violation.
- **The full Brands menu** in Seller Central, and eligibility for programs that require the brand's own account.

## When it fits

- Bison already sells a meaningful share of your brand on Amazon.
- Bison maintains your listing content and advertising.
- You want the brand defended against copied listings and unauthorized sellers.

## How to grant it

The same path as the Reseller Role: your Registry Administrator opens **Manage, then Manage Selling Accounts, then Connect a selling account** and assigns **Brand representative** to Bison's account. No cost and no change to the agreement.

## Who does what

- **Data.** Bison uses Brand Analytics for keywords, assortment and repeat-purchase insight, and shares what it finds with you.
- **Content.** Bison maintains the listing content. You set the brand-level direction.
- **Protection.** Bison reports violations and copied listings. You stay the Administrator and keep ownership of the brand.`,
    },
];

const SCALE: Program[] = [
    {
        id: "new-sku-launch",
        title: "New SKU Launch Program",
        tier: "growth", // Growth since 2026-10-25 (Bill)
        icon: "rocket.svg",
        summary:
            "Gets your new products live and selling across marketplaces: Amazon, Walmart, Target Plus, Lowe's, Macy's, Best Buy, and more. It pairs category-correct content with launch-period investment so a new listing climbs out of the cold start fast instead of languishing with no reviews and no rank.",
        body: `## Purpose

A new product on a marketplace starts invisible, with no reviews, no rank, and no history. A disciplined launch (correct category, complete content, and concentrated early investment) is what turns a brand-new SKU into a ranked, converting listing in weeks instead of quarters.

## When it fits

- You are adding new products to the assortment or expanding an existing line.
- An existing product needs to be opened up on additional marketplaces.
- A best-selling collection is identified that is not yet live everywhere it could sell.

## How it works

1. **Classify and route.** Each product is categorized and routed to the correct marketplace template.
2. **Fill content to channel rules.** Bison populates title, images, dimensions, brand, and category-specific attributes, enforcing the non-negotiables: white-background hero image, brand-prefixed title, variant grouping, image-count minimums, and banned-word compliance.
3. **Validate before upload.** Products that fall short of image minimums or carry banned claims are caught and sent back for inputs rather than shipped broken.
4. **Launch and ramp.** Once live, the listing gets early advertising and optimization to build the first reviews and rank.

## Content rules that matter

- Brand prefix on the title (with channel exceptions where the platform injects it).
- Variant grouping so color and size families coalesce into one listing.
- Banned claims stay out: unsubstantiated terms like "sustainable," "organic," or "free of."
- Image minimums per channel (for example, three images minimum on several marketplaces).

## Who does what

- **Content.** Bison categorizes, routes to templates, fills content, and enforces the rules. You provide accurate product data and assets.
- **Assets.** Bison flags missing or non-compliant images before upload. You supply the white-background hero and any missing imagery.
- **Launch.** Bison invests in early advertising and optimization to build rank. You ensure availability so the launch is not starved of stock.`,
    },
    {
        id: "review-acceleration",
        title: "Review Acceleration Program",
        tier: "scale",
        icon: "star-plus.svg",
        summary:
            "Grows a product's review count and rating through several compliant channels at once: Amazon Vine, in-box inserts, outreach to Bison's customer base, promotional pricing, and launch-period advertising. Reviews are social proof, and social proof drives conversion.",
        body: `## Purpose

Shoppers trust reviews, and the algorithm rewards them. A new or thinly-reviewed listing converts a fraction of what a well-reviewed one does. Accelerating reviews early breaks that chicken-and-egg loop and lets a good product earn the trust it deserves faster.

## When it fits

- A newly launched product has few or no reviews and needs social proof to convert.
- A strong product is under-reviewed relative to its category competitors.
- You are already investing in advertising and want reviews to compound the return on that traffic.

## How it works

The program stacks several compliant tactics:

- **Amazon Vine.** Enroll eligible FBA SKUs; Amazon ships free product to its reviewer pool in exchange for honest reviews. Vine costs roughly $200 per ASIN plus the free units, is FBA-only, and works best on smaller, higher-volume, lower-price items in crowded categories.
- **In-box inserts.** A branded insert with a QR code to a care guide and a soft ask for feedback (compliant, never an incentive).
- **Customer-base outreach.** Single-use discount codes to existing customers shortly after launch, reaching buyers more likely to review.
- **Promotions plus advertising.** Maximize sales volume early so organic reviews accumulate faster.

## Who does what

- **Setup.** Bison enrolls Vine, designs inserts, and runs the promo and ad calendar. You provide free product for Vine and approve promotional pricing.
- **Funding.** Bison allocates and manages launch ad spend. You co-fund advertising per the agreement.
- **Monitoring.** Bison tracks review count, rating, and sentiment over the launch window. You act on product or listing issues surfaced in reviews.

## What good looks like

- Review count growing steadily month over month through the first six months post-launch.
- Average rating holding at or above 4.0 as volume builds.`,
    },
    {
        id: "social-media",
        title: "Social Media Program",
        tier: "scale",
        icon: "msg-heart.svg",
        summary:
            "Drives external traffic and brand awareness to your marketplace listings through social platforms, influencer partnerships, and affiliate networks: TikTok, Instagram, Facebook, Pinterest, and creator collaborations. It brings demand from outside the marketplace to listings that are ready to convert.",
        body: `## Purpose

Not all demand starts with a marketplace search. Social and creator content puts a product in front of buyers who were not looking for it yet, builds brand recognition that lifts conversion everywhere, and sends external traffic that marketplaces reward with better rank.

## When it fits

- A product is visually compelling or has a clear lifestyle story that performs on social.
- You want to build brand awareness beyond pure marketplace search demand.
- Listings are already optimized and review-ready, so the traffic we send actually converts.

## How it works

- **Organic and paid social** across TikTok, Instagram, Facebook, and Pinterest, with content built for each platform.
- **Influencer partnerships** that put the product in trusted creators' hands for authentic content.
- **Affiliate networks** that reward partners for the sales they bring.
- Traffic is pointed at the marketplace listing (or a Bison direct channel), with the listing optimized to convert it.

## Who does what

- **Strategy.** Bison plans the channel mix, content, and creator partnerships. You supply product, brand assets, and any creator samples.
- **Execution.** Bison produces and runs campaigns and manages affiliates and influencers. You align on brand voice and positioning.
- **Measurement.** Bison tracks traffic, engagement, and downstream sales lift. You ensure stock to meet demand spikes.

## What good looks like

- Social and creator content that sends measurable external traffic to listings.
- Engagement that translates into downstream marketplace sales rather than views alone.
- Growing brand recognition that lifts conversion on the listing even for non-social traffic.`,
    },
    {
        id: "seller-fulfilled-prime",
        title: "Seller Fulfilled Prime Program",
        tier: "scale",
        icon: "cart-fast.svg",
        summary:
            "Puts the Prime badge on listings that ship from your own warehouse instead of from Amazon's fulfillment centers. You (with Bison managing the program) earn Prime eligibility by meeting Amazon's delivery, tracking, and cancellation standards on every order.",
        body: `## Purpose

The Prime badge is one of the biggest conversion factors on Amazon, but FBA is impractical for much of the big-and-bulky catalogue. SFP captures the Prime conversion lift while keeping fulfillment in your warehouse, for furniture that does not belong in an Amazon fulfillment center.

## When it fits

- You ship from a regional warehouse with reliable, fast carrier coverage and disciplined operations.
- Products are too large or too slow-moving for FBA to make sense, but would benefit hugely from the Prime badge.
- The warehouse can consistently hit Amazon's on-time and cancellation bars, week in and week out.

## How it works

1. **Qualify and trial.** Enroll in the SFP trial: over a four-week period, ship at least 100 Prime packages while meeting every performance bar. Up to three trial attempts are allowed per year.
2. **Configure shipping.** Set up SFP shipping templates offering free nationwide standard delivery to the contiguous US, with the required delivery-speed mix by size tier.
3. **Fulfill to standard.** Ship from your warehouse using Amazon Buy Shipping (or a certified carrier) so tracking and on-time data flow back automatically.
4. **Maintain eligibility.** Performance is reviewed weekly and per size tier. Hold the standards and the Prime badge stays; miss them and it can be suspended.

## Performance requirements

- **On-time delivery:** 93.5% or higher
- **Valid tracking rate:** 99% or higher
- **Cancellation rate:** 0.5% or lower
- **Minimum volume:** 100+ Prime packages per month
- **Nationwide coverage:** free standard shipping to all 48 contiguous states plus DC (mandatory)

Delivery-speed targets vary by size tier (standard, oversize, extra-large), with faster promises required on smaller items.

## Who does what

- **Program.** Bison manages enrollment, trial, shipping templates, and performance monitoring. You operate the warehouse and ship every order to standard.
- **Logistics.** Bison sets up Buy Shipping and tracking integration. You maintain carrier capacity and on-time fulfillment.
- **Compliance.** Bison watches the weekly per-tier metrics and flags risk early. You hold the line on cancellations and delivery speed.`,
    },
    {
        id: "backorders",
        title: "Backorders Program",
        tier: "growth", // Growth since 2026-10-25 (Bill)
        icon: "hourglass.svg",
        summary:
            "Keeps your Amazon listings buyable through a short stockout. When a product runs out but your restock date is close, Bison keeps selling it with a later delivery date and sends you the order when the stock arrives.",
        body: `## Purpose

A listing that goes dark stops selling and loses the standing it built. Backorders trades a longer delivery promise for keeping the listing live and keeping some sales while you restock.

## When it fits

- Your inventory feed carries a restock date for out-of-stock items, refreshed daily, and the dates hold.
- You accept backorders across the whole assortment, not just a few products.
- Incoming stock is held for backorders, not sold to whoever orders next.

## How it works

1. Bison switches your account on for backorders (Amazon only).
2. A product goes out of stock, and your feed gives a restock date.
3. If that date is inside the agreed window, the listing stays live and Amazon gets a restock date of your date plus a buffer.
4. A customer can still buy. The order comes to Bison marked as a backorder.
5. Bison releases the order to you to ship when the stock lands.
6. When stock returns, the listing goes back to normal on its own.

If the restock date is missing or too far out, nothing changes and the listing goes out of stock as before.

## Standard settings

- **Restock window:** 10 days. A product qualifies only while its restock date is within 10 days.
- **Delivery buffer:** 14 days, added to your restock date before it goes to Amazon.

For example, a restock date 8 days out keeps the listing live with a delivery date built from 22 days out. A wider window is possible, agreed case by case, because restock dates further out are less certain.

## Who does what

- **Setup.** Bison runs a short questionnaire with you, switches you on and sets the window. You confirm how you handle restock dates and backorders.
- **Data.** You send an accurate restock date for every out-of-stock item, every day. Bison works out which products qualify as stock and dates change.
- **Fulfillment.** You hold incoming stock for backorders and ship them by the promised date. Bison flags any backorder at risk of shipping late each day and follows up with you.

## What good looks like

- Every backorder ships by the date promised to the customer.
- No cancellations from backorders. Amazon counts each one against the whole account, not just the listing.`,
    },
];

const STRATEGIC: Program[] = [
    {
        id: "inventory",
        title: "Inventory Program",
        tier: "strategic",
        icon: "storage-shelf.svg",
        summary:
            "How Bison protects your marketplace performance from the swings of your supply chain: reporting inventory accurately by warehouse, keeping listings buyable through restock gaps, and making delivery promises you can actually keep. The goal is to break the link between a stock-out and lost rank, lost Buy Box, and lost revenue.",
        body: `## Purpose

On a marketplace, an inventory problem becomes a visibility problem fast. A listing that goes dark during a restock, or promises two-day delivery it cannot meet, loses rank and Buy Box that take weeks to win back. Managing inventory intelligently keeps listings live, promises honest, and revenue protected through the gaps.

## When it fits

- You ship from multiple warehouses and aggregate inventory reporting is producing inaccurate delivery estimates.
- Listings go dark during restocks, dropping out of the Buy Box and losing session traffic every time stock hits zero.
- You have reliable restock ETAs that could keep listings buyable if the marketplace actually used them.

## How it works

- **Multi-Location Inventory (MLI).** Reports inventory per warehouse and assigns warehouse-specific shipping templates, so a customer sees an accurate delivery date for the warehouse that will actually ship to them.
- **Backorder ETA protection.** When a SKU is out of stock but has a confirmed restock date, the listing stays buyable showing "ships by" with the restock date instead of going inactive, capturing orders through the gap.

Both depend on accurate per-warehouse inventory and honest restock dates flowing from you into the marketplace.

## Example in action

In a six-week pilot, routing delivery promises to the correct warehouse rather than an aggregate pool cut late deliveries and lifted margin: roughly 0.9 points of margin and around $53K of revenue over three weeks, a 35% lift in daily revenue.

## Guardrails

- **ETA accuracy floor:** only suppliers running roughly 80%+ historical ETA accuracy qualify for backorder listing protection. Bad ETAs cause late shipments and returns, so the floor is a hard gate.
- **Handling window:** restock dates must fall inside Amazon's handling cap (about a 21-day activation window against Amazon's 30-day maximum).

## Who does what

- **Reporting.** Bison reports inventory per warehouse and assigns shipping templates. You provide accurate per-warehouse inventory feeds and location data.
- **Restocks.** Bison keeps listings buyable through restock gaps using confirmed ETAs. You supply realistic restock dates and hit them.
- **Monitoring.** Bison tracks on-time delivery, ETA accuracy, and out-of-stock rate. You maintain supply discipline and flag changes early.

## What good looks like

- Out-of-stock rate under 5%, with backorder protection covering the gaps that remain.
- ETA accuracy at 80%+, met within a few days of the promised date.
- Buy Box and session traffic holding steady through restocks instead of collapsing each cycle.`,
    },
    {
        id: "private-label",
        title: "Private Label Program",
        tier: "strategic",
        icon: "diamond.svg",
        summary:
            "Bison developing, sourcing, and selling products under Bison's own brands (such as Heartland Furniture). You manufacture the goods; Bison owns the brand, the inventory, the SKUs, and the listings, and carries the inventory risk. This is a product-ownership play, the deepest form of partnership in the portfolio.",
        body: `## Purpose

Private label is where Bison stops being only a seller of other people's products and becomes a brand owner. By using your manufacturing capacity to build Bison-owned products, Bison captures full margin, controls the entire customer experience, and builds lasting brand equity.

## When it fits

- You have manufacturing capacity (and ideally excess capacity) and are open to making product to Bison's spec.
- There is a clear market gap where a Bison-owned brand can compete on price and margin.
- Bison can commit the working capital to buy and hold inventory and the operational capacity to manage it across channels.

## How it works

1. **Source.** Bison identifies a supplier with capacity, negotiates a private-label partnership, and places a purchase order. You manufacture to Bison's specification.
2. **Receive and hold.** Bison takes inventory in at its own risk, by the container, holding it in supplier or Bison-managed warehouses.
3. **Launch across channels.** Bison creates content, sets channel-specific pricing, and advertises (Amazon, Wayfair, and others).
4. **Manage by sell-through.** Reorder decisions are driven by how fast inventory moves, with seasonal lead times planned months ahead (order by late spring for the autumn and holiday peak).

## Reorder logic

Private label is a buy-and-hold model, not an allowance. The figure that matters is sell-through (units sold divided by units received):

- **80%+:** reorder immediately, a proven winner.
- **60-80%:** reorder with adjusted quantity.
- **40-60%:** evaluate pricing before committing more capital.
- **Under 40%:** do not replenish; manage down the remaining stock.

## Who does what

- **Product.** Bison owns the brand, specs the product, and places and funds purchase orders. You manufacture to spec and honor lead times.
- **Inventory.** Bison holds inventory, makes reorder decisions, and manages dead stock. You supply accurate cost, tariff, and lead-time data.
- **Market.** Bison creates content, sets pricing, and runs advertising across channels. You provide a white-label agreement and product rights.`,
    },
    {
        id: "managed-seller-services",
        title: "Managed Seller Service Program",
        tier: "strategic",
        icon: "mountain.svg",
        documents: [
            { title: "Managed Seller Services", url: "/program-docs/managed-seller-services.pdf" },
        ],
        summary:
            "Bison operating your own marketplace seller account as a full-service agency. You keep ownership of the account; Bison runs the day-to-day: listings, pricing, advertising, inventory sync, compliance, and customer support. It is distinct from the core model where Bison buys and resells; here you sell, and Bison is the operator.",
        body: `## Purpose

Many suppliers have a marketplace account but lack the team, tools, and time to run it well. MSS lets you rent Bison's entire operating machine, real-time pricing across marketplaces, integrated advertising, logistics scale, and compliance automation, while keeping your own account and brand.

## When it fits

- You want to keep your own seller account and brand but cannot operate it at the level it deserves.
- The account has real potential being left on the table for lack of operating capability.
- You value a partner who can run pricing, ads, and compliance better than an in-house team, and are comfortable delegating day-to-day decisions within agreed guardrails.

## How it works

MSS sits at the top of a deliberate progression: 3P reseller, then full-assortment exclusivity, then MSS or inventory plays.

1. **Onboard the account.** Bison takes operational control of your seller account within agreed guardrails.
2. **Run operations.** Listings, pricing, advertising, inventory syncs, compliance, and customer-support escalations are all handled by Bison's team and tooling.
3. **Report and steer.** Bison reports performance back to you and steers strategy, while you retain ownership and final say on brand-level decisions.

## Commercial model

MSS is a services revenue model rather than an allowance or a resale margin. Specific fee structures are set per engagement.

## Who does what

- **Operations.** Bison runs listings, pricing, ads, inventory sync, compliance, and support. You own the account and set brand-level direction.
- **Data.** Bison executes against your product and inventory data. You provide accurate data and flag assortment or supply changes.
- **Governance.** Bison operates within agreed pricing and advertising guardrails. You approve the guardrails and review performance.

## How MSS differs from adjacent programs

- **vs Exclusivity:** Exclusivity makes Bison the sole seller of your products on Bison's own account; MSS has Bison operate your own account and brand.
- **vs Private / White Label:** those are Bison-owned products or brands; MSS is entirely your brand and account, with Bison as the operator.
- **vs the reseller model:** Bison takes no inventory ownership or resale margin in MSS; it earns a service fee for running the account.

## What good looks like

- Account-level Buy Box ownership and a clear sales-growth trajectory under Bison's operation.
- Healthy account margin after marketplace commissions and the service fee.
- Strong customer-experience signals: returns rate, review trend, and on-time delivery compliance.
- Zero compliance violations, the non-negotiable baseline when operating someone else's account.`,
    },
];

// Display order (Bill, 2026-10-25). Growth: Backorders first, the commercial
// programs, the brand permissions (which come before big listing changes),
// then the listing work. Tiles wrap six to a row, so Brand Registry ends row
// one. 1P to 3P
// closes Scale.
const ORDER = [
    "backorders", "white-label", "rebate", "exclusivity", "trademark", "brand-registry",
    "reseller-role", "brand-representative-role", "new-sku-launch", "listing-optimization", "bundles-and-sets",
    "review-acceleration", "social-media", "seller-fulfilled-prime", "1p-to-3p-conversion",
];
const rank = (p: Program) => ORDER.indexOf(p.id ?? "");

export const CANONICAL_PROGRAMS_EXPLORER_PROPS: WidgetOfType<"programs_explorer">["props"] = {
    programs: [...FOUNDATION, ...[...GROWTH, ...SCALE].sort((x, y) => rank(x) - rank(y)), ...STRATEGIC],
};
